import React, { useState } from 'react';
import { STUDIO_COPY } from '../config';
import {
  REEL_DOWNLOAD_FILENAME,
  prefersMobileReelSave,
  reelSaveProgressIndeterminate,
  reelSaveWorkingLabel,
  saveReelToDevice,
  type ReelFetchProgress,
} from '../utils/saveReelToDevice';

type ReelDownloadButtonProps = {
  url: string;
  className?: string;
  testId: string;
};

/**
 * Desktop: native `<a download>`. Mobile/PWA: Web Share file sheet, with
 * open-in-tab fallback when share or the blob fetch is unavailable.
 */
const ReelDownloadButton: React.FC<ReelDownloadButtonProps> = ({
  url,
  className,
  testId,
}) => {
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState<ReelFetchProgress | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const mobileSave = prefersMobileReelSave();

  if (!url) return null;

  if (!mobileSave) {
    return (
      <a
        href={url}
        download={REEL_DOWNLOAD_FILENAME}
        className={className}
        data-testid={testId}
      >
        {STUDIO_COPY.downloadLabel}
      </a>
    );
  }

  const handleClick = async () => {
    if (working) return;
    setHint(null);
    setProgress(null);
    setWorking(true);
    try {
      const result = await saveReelToDevice(url, (next) => {
        setProgress({ loaded: next.loaded, total: next.total });
      });
      if (result === 'opened') {
        setHint(STUDIO_COPY.downloadHoldHint);
      }
    } finally {
      setWorking(false);
      setProgress(null);
    }
  };

  const indeterminate = working && reelSaveProgressIndeterminate(progress);
  const label = working
    ? reelSaveWorkingLabel(STUDIO_COPY.downloadWorking, progress)
    : STUDIO_COPY.downloadLabel;

  return (
    <span className="studio-download-action">
      <button
        type="button"
        className={className}
        onClick={() => void handleClick()}
        disabled={working}
        aria-busy={working}
        data-testid={testId}
      >
        {indeterminate ? (
          <span
            className="studio-spinner studio-spinner--sm"
            aria-hidden
            data-testid={`${testId}-spinner`}
          />
        ) : null}
        {label}
      </button>
      {hint ? (
        <p
          className="studio-download-hold-hint"
          role="status"
          data-testid={`${testId}-hint`}
        >
          {hint}
        </p>
      ) : null}
    </span>
  );
};

export default ReelDownloadButton;
